import { useEffect, useRef } from 'react'

import { FormType } from '#/shared/api/generated/shipment/models'
import {
  useGetFormPreferenceApiV1FormPreferencesFormTypeGet,
  useUpsertFormPreferenceApiV1FormPreferencesFormTypePut,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { useDebouncedValue } from '#/shared/lib/hooks'

import { ALL_MANAGE_FIELD_KEYS } from './keys'
import type { ManageFieldKey } from './keys'
import { useManageFieldsStore } from './store'

const KNOWN_KEYS = new Set<string>(ALL_MANAGE_FIELD_KEYS)

function hiddenToFields(
  hidden: Partial<Record<ManageFieldKey, true>>,
): string[] {
  return Object.keys(hidden).filter((key) => hidden[key as ManageFieldKey])
}

function serialize(fields: string[]): string {
  return JSON.stringify([...fields].sort())
}

export function useFormPreferenceSync(
  formType: FormType | null | undefined,
): void {
  const enabled = formType != null
  const query = useGetFormPreferenceApiV1FormPreferencesFormTypeGet(
    formType ?? FormType.order,
    { query: { enabled } },
  )
  const { mutateAsync } =
    useUpsertFormPreferenceApiV1FormPreferencesFormTypePut()
  const hidden = useManageFieldsStore((state) => state.hidden)
  const debouncedHidden = useDebouncedValue(hidden, 600)
  const syncedRef = useRef<string | null>(null)

  useEffect(() => {
    syncedRef.current = null
  }, [formType])

  const settled = query.isSuccess || query.isError
  useEffect(() => {
    if (!enabled || !settled || syncedRef.current !== null) return
    const serverFields = query.data?.data.hidden_fields?.filter((key) =>
      KNOWN_KEYS.has(key),
    )
    if (serverFields) {
      syncedRef.current = serialize(serverFields)
      useManageFieldsStore.setState({
        hidden: Object.fromEntries(serverFields.map((key) => [key, true])),
      })
    } else {
      syncedRef.current = serialize(
        hiddenToFields(useManageFieldsStore.getState().hidden),
      )
    }
  }, [enabled, settled, query.data])

  useEffect(() => {
    if (!enabled || syncedRef.current === null) return
    const fields = hiddenToFields(debouncedHidden)
    const next = serialize(fields)
    if (next === syncedRef.current) return
    syncedRef.current = next
    void mutateAsync({ formType, data: { hidden_fields: fields } })
  }, [enabled, formType, debouncedHidden, mutateAsync])
}
